import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Popover,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowsOutCardinal,
  Warning,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import React, { memo, useEffect, useState } from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { FlowService } from '../../../service/FlowService';
import { useAuthStore } from '../../../store/storeAuth';
import { useDebounceEffect } from '../../../util/useDebounceEffect';
import FormNode from '../Components/FormNode/FormNode';
import FormBuildInput from '../FormBuildInput/FormBuildInput';
import PopoverEdit from '../TitleNode/Components/PopoverEdit';

const fileTypes = ['PDF', 'TXT', 'DOCX', 'PNG', 'JPEG', 'JPG', 'PPTX'];

const InputNodeDisplay = ({ data, id, type }: NodeProps) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    url: '',
    borderRadius: '4px',
    selectedFlow: '',
    ruleVarInputNode: '',
    required: '',
    isEdge: false,
    fileTypesSelected: fileTypes,
    lineLength: '3',
    titleStyle: {
      fontSize: '20px',
      color: '#475467',
      textAlign: 'center',
      fontStyle: 'normal',
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 500,
      textDecoration: 'none',
      selStyle: [],
    },
    subtitleStyle: {
      fontSize: '14px',
      color: '#475467',
      textAlign: 'center',
      fontStyle: 'normal',
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      textDecoration: 'none',
      selStyle: [],
    },
    varInputNodes: [],
  });
  const reactFlow = useReactFlow();
  const { user } = useAuthStore(state => state);

  const [changes, setChanges] = useState<any>({
    directly: false,
    value: '',
    name: '',
  });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState('titleStyle');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedNode, setSelectedNode] = useState({
    id: '',
    flowId: '',
    name: '',
    inputDataOnly: false,
    uploadOnly: false,
    inputBoth: false,
    mode: '',
    lineLength: '',
    required: '',
  });
  const nodes = reactFlow.getNodes()?.map(node => {
    if (node?.type === 'inputDisplay' && node?.id !== id) {
      return {
        id: node?.id,
        label: node?.data?.label,
        selectedFlow: node?.data?.selectedFlow,
      };
    }
  });

  useEffect(() => {
    if (data && !changes?.directly) {
      const selectedFlow =
        data?.selectedFlow && data?.varInputNodes?.length > 0
          ? data?.selectedFlow
          : !data?.selectedFlow && data?.varInputNodes?.length > 0
          ? 'none'
          : 'none';
      setFormData({
        title: data.title || '',
        subtitle: data.subtitle || '',
        borderRadius: data.borderRadius || '4px',
        ruleVarInputNode: data.ruleVarInputNode || '',
        required: data.required || '',
        url: data.url || '',
        selectedFlow: selectedFlow,
        isEdge: data.isEdge || false,
        fileTypesSelected: data.fileTypesSelected || [],
        varInputNodes: data.varInputNodes || ['PDF', 'TXT'],
        lineLength: data.lineLength !== undefined ? data.lineLength : '3',
        titleStyle: {
          ...formData?.titleStyle,
          ...data.titleStyle,
        },
        subtitleStyle: {
          ...formData?.subtitleStyle,
          ...data.subtitleStyle,
        },
      });
    }
  }, [data, changes]);

  useDebounceEffect(
    () => {
      if (changes.directly) {
        const nodes = reactFlow.getNodes();

        const mappedNodes = nodes.map(node => {
          if (node.id === id) {
            if (changes?.data?.widthData) {
              return {
                ...node,
                width: changes.data.widthData?.width,
                style: {
                  width: changes.data.widthData?.width,
                },
                data: {
                  ...node.data,
                  widthData: changes.data.widthData,
                },
              };
            }

            if (changes?.name === 'widthData') {
              return {
                ...node,
                width: changes.value?.width,
                style: {
                  width: changes.value?.width,
                },
                data: {
                  ...node.data,
                  [changes.name]: changes.value,
                },
              };
            }

            return {
              ...node,
              data: {
                ...node.data,
                ...changes.data,
              },
            };
          }
          return node;
        });

        reactFlow.setNodes(mappedNodes);

        setChanges({ directly: false });
      }
    },
    [changes],
    500,
  );

  const onChanges = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    const data = {
      [name]: value,
    };

    setFormData({ ...formData, ...data });
    setChanges({ directly: true, data });
  };

  const handleClick = (type: string) => {
    setOpenEditModal(true);
    setEditingType(type);
  };

  const handleClose = () => {
    setOpenEditModal(false);
  };

  const handleClickPopover = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (node: any) => {
    setSelectedNode(prev => ({
      id: node.id,
      flowId: node.flowId,
      name: node.label,
      inputDataOnly: formData.ruleVarInputNode === 'text',
      uploadOnly: formData.ruleVarInputNode === 'upload',
      inputBoth: formData.ruleVarInputNode === 'both',
      mode: formData.ruleVarInputNode || '',
      lineLength: formData.lineLength || '3',
      required: prev.required || formData.required || 'no',
    }));
  };

  const handleModeChange = (event: any) => {
    const value = event.target.value;

    setSelectedNode(prev => ({
      ...prev,
      inputDataOnly: value === 'text',
      uploadOnly: value === 'upload',
      inputBoth: value === 'both',
      mode: value,
    }));
  };

  const handleRequiredChange = (event: any) => {
    const value = event.target.value;
    console.log('value', value);

    setSelectedNode(prev => ({
      ...prev,
      required: value,
    }));
  };

  const handleChangeFileTypes = (event: any) => {
    const {
      target: { value },
    } = event;

    const data = {
      fileTypesSelected: value,
    };

    setFormData({ ...formData, ...data });
    setChanges({ directly: true, data });
  };

  async function handleSubmitSelectedNode() {
    const response = await FlowService.getFlow(selectedNode.flowId);
    const flow = response?.data;

    const isEdge = flow.edges.some(
      (edge: { source: string; target: string }) =>
        edge.source === selectedNode.id,
    );

    const data = {
      selectedFlow: selectedNode.id,
      ruleVarInputNode: selectedNode.mode,
      required: selectedNode.required,
      isEdge,
    };
    setFormData({ ...formData, ...data });
    setChanges({ directly: true, data });

    setAnchorEl(null);
  }

  const hasInputNode = data?.varInputNodes?.length > 0;

  useEffect(() => {
    if (data?.selectedFlow && data?.varInputNodes?.length > 0) {
      const selectedInputNode = data.varInputNodes.find(
        (node: any) => node.id === data.selectedFlow,
      );

      if (selectedInputNode) {
        setSelectedNode(prev => ({
          id: selectedInputNode.id,
          flowId: selectedInputNode.flowId,
          name: selectedInputNode.label,
          inputDataOnly: data.ruleVarInputNode === 'text',
          uploadOnly: data.ruleVarInputNode === 'upload',
          inputBoth: data.ruleVarInputNode === 'both',
          mode: data.ruleVarInputNode || '',
          lineLength: formData.lineLength || data.lineLength || '3',
          required: prev.required || data.required || '',
        }));
      }
    }
  }, [data?.selectedFlow, data?.varInputNodes, data?.ruleVarInputNode]);

  return (
    <>
      <FormNode borderRadius={formData.borderRadius} id={id} type={type}>
        <FormBuildInput
          data={data}
          formData={{
            name: 'title',
            value: formData.title,
            placeholder: 'Add title here...',
            style: formData.titleStyle,
            titleEmpty: 'Title',
          }}
          editingType={editingType}
          editType={'titleStyle'}
          handleClick={handleClick}
          isEditing={isEditing}
          onChanges={onChanges}
          setEditingType={setEditingType}
          setIsEditing={setIsEditing}
        />

        <FormBuildInput
          data={data}
          formData={{
            name: 'subtitle',
            value: formData.subtitle,
            placeholder: 'Add subtitle here...',
            style: formData.subtitleStyle,
            titleEmpty: 'Subtitle (Optional)',
          }}
          editingType={editingType}
          editType={'subtitleStyle'}
          handleClick={handleClick}
          isEditing={isEditing}
          onChanges={onChanges}
          setEditingType={setEditingType}
          setIsEditing={setIsEditing}
        />

        {openEditModal && (
          <PopoverEdit
            open={openEditModal}
            handleClose={handleClose}
            onChange={onChanges}
            selectedStyle={formData?.[editingType as keyof typeof formData]}
            editingType={editingType}
            modalStyle={{
              bgColor: data?.bgColor ? data?.bgColor : '#ffffff',
              widthData: data?.widthData,
              borderRadius: formData.borderRadius || '4px',
            }}
          />
        )}

        {/* <Box
          sx={{
            border: `1px solid var(--Neutral-300, #EAECF0)`,
            borderRadius: '4px',
            padding: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: '16px',
            mb: '24px',
          }}
        >
          <Box textAlign={'center'}>
            <BoxArrowDown size={24} color="#667085" />
            <Typography fontSize={'12px'} color={'#475467'}>
              Input your content that you want to run through your Ai flow. To
              test this, enter preview mode or publish your form.
            </Typography>
          </Box>
        </Box> */}

        {hasInputNode ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              mt: '16px',
            }}
          >
            <Box display={'flex'} alignItems={'center'} gap="8px">
              <WarningCircle size={16} color="#667085" />
              <Typography
                variant="body2"
                sx={{
                  color: '#475467',

                  fontSize: '12px',
                  fontWeight: 400,
                }}
              >
                Please select which
                <Typography
                  component={'span'}
                  fontWeight={600}
                  fontSize={'12px'}
                >
                  "Data Input"
                </Typography>{' '}
                to use for this input field.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              color="primary"
              onClick={handleClickPopover}
              sx={{
                width: '200px',
                fontSize: '12px',
              }}
            >
              {formData.selectedFlow !== 'none'
                ? (
                    formData.varInputNodes.find(
                      (node: any) => node.id === formData.selectedFlow,
                    ) as { label: string } | undefined
                  )?.label || 'Choose Data Input Node'
                : 'Choose Data Input Node'}
            </Button>

            {!formData.isEdge && formData.selectedFlow !== 'none' && (
              <Box display={'flex'} alignItems={'center'} gap="8px">
                <Warning size={16} color="#F57F17" weight="fill" />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#475467',

                    fontSize: '12px',
                    fontWeight: 400,
                  }}
                >
                  This Data Input field will{' '}
                  <Typography
                    component={'span'}
                    fontWeight={600}
                    fontSize={'12px'}
                  >
                    NOT
                  </Typography>{' '}
                  allow users to upload files. Change this on the Data Input
                  node In your flow.
                </Typography>
              </Box>
            )}

            {formData.ruleVarInputNode === 'text' &&
              formData.selectedFlow !== 'none' &&
              formData.isEdge && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#475467',
                    fontSize: '12px',
                    fontWeight: 400,
                  }}
                >
                  This Data Input field is set up to appear as a text input box
                  on the form.
                </Typography>
              )}

            <Box
              display={'flex'}
              gap={'8px'}
              alignItems={'center'}
              width={'100%'}
            >
              {formData.ruleVarInputNode !== 'text' &&
                formData.selectedFlow !== 'none' &&
                formData.isEdge && (
                  <FormControl sx={{ width: '100%', mt: '12px' }}>
                    <InputLabel id="select-label" sx={{ mt: '-8px' }}>
                      Choose files extensions
                    </InputLabel>
                    <Select
                      labelId="select-label"
                      className="nodrag"
                      size="small"
                      fullWidth
                      multiple
                      value={formData.fileTypesSelected}
                      sx={{
                        height: 'unset',
                        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      }}
                      onChange={handleChangeFileTypes}
                      input={<OutlinedInput label="Choose files extensions" />}
                      renderValue={selected => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((value: any) => (
                            <Chip size="small" key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {fileTypes.map((name: any) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

              {(selectedNode.mode === 'text' ||
                selectedNode.mode === 'both') && (
                <FormControl sx={{ width: '100%', mt: '12px' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="outlined-basic"
                    value={formData.lineLength}
                    onChange={onChanges}
                    size="small"
                    name="lineLength"
                    label="Default Row Count"
                    sx={{
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                  />
                </FormControl>
              )}
            </Box>
          </Box>
        ) : (
          <Box display={'flex'} alignItems={'center'} gap="8px">
            <Warning size={16} color="#F57F17" weight="fill" />
            <Typography
              variant="body2"
              sx={{
                color: '#475467',

                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              Please connect a{' '}
              <Typography component={'span'} fontWeight={600} fontSize={'12px'}>
                Data Input Node
              </Typography>{' '}
              to your workflow first.
            </Typography>
          </Box>
        )}
      </FormNode>

      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          width={'629px'}
          height={'100%'}
          p={'16px'}
          borderRadius="16px"
          gap={'24px'}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <ArrowsOutCardinal size={16} color="#101828" />
                <Typography
                  fontSize={'14ox'}
                  fontWeight={600}
                  color={'#000000'}
                >
                  Choose Input
                </Typography>
              </Box>
              <XCircle
                size={24}
                color="#667085"
                cursor={'pointer'}
                onClick={() => setAnchorEl(null)}
              />
            </Box>

            <Typography fontSize={'14px'} fontWeight={400} color={'#475467'}>
              Select the Input API node that meets your form's data needs. It
              will provide the required input when your form is published.
            </Typography>
          </Box>

          <Box
            display={'flex'}
            flexDirection={'column'}
            gap={'8px'}
            sx={{
              overflowY: 'auto',
              maxHeight: '250px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#F2F4F7',
                padding: '2px',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#98A2B3',
                borderRadius: '4px',
              },
            }}
          >
            {data?.varInputNodes?.map((node: any, index: number) => {
              const isSelected = selectedNode?.id === node.id;

              return (
                <>
                  <Box
                    onClick={() => handleSelect(node)}
                    key={node?.id}
                    sx={{
                      display: 'flex',
                      padding: '8px',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      border: isSelected
                        ? '1px solid #D0D5DD'
                        : '1px solid transparent',
                      backgroundColor: isSelected ? '#F9FAFB' : 'transparent',
                      borderRadius: '8px',
                      mr: '4px',
                    }}
                  >
                    <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: isSelected ? '#101828' : '#475467',
                          fontWeight: 400,
                        }}
                      >
                        {node?.label}
                      </Typography>

                      {isSelected && selectedNode.mode === 'text' && (
                        <Tooltip
                          title="This Data Input field will NOT allow users to upload files. "
                          placement="top"
                          arrow
                        >
                          <Warning size={16} color="#F57F17" weight="fill" />
                        </Tooltip>
                      )}
                    </Box>

                    {isSelected && (
                      <>
                        <RadioGroup
                          value={selectedNode.mode}
                          onChange={handleModeChange}
                          row
                          sx={{ display: 'flex', gap: '8px' }}
                        >
                          <FormControlLabel
                            value="text"
                            control={<Radio size="small" />}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                              },
                              color: '#101828',
                            }}
                            label="Input data only"
                          />

                          <FormControlLabel
                            value="upload"
                            control={<Radio size="small" />}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                              },
                              color: '#101828',
                            }}
                            label="Uploads only"
                          />

                          <FormControlLabel
                            value="both"
                            control={<Radio size="small" />}
                            sx={{
                              '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                              },
                              color: '#101828',
                            }}
                            label="Input both"
                          />
                        </RadioGroup>

                        <div style={{ marginTop: '8px' }}>
                          <span
                            id="demo-radio-buttons-group-label"
                            style={{
                              color: '#101828',
                              fontSize: '14px',
                            }}
                          >
                            Is this field required?
                          </span>
                          <RadioGroup
                            value={selectedNode.required}
                            onChange={handleRequiredChange}
                            row
                            sx={{ display: 'flex', gap: '8px' }}
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio size="small" />}
                              sx={{
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '14px',
                                },
                                color: '#101828',
                              }}
                              label="Yes"
                            />

                            <FormControlLabel
                              value="no"
                              control={<Radio size="small" />}
                              sx={{
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '14px',
                                },
                                color: '#101828',
                              }}
                              label="No"
                            />
                          </RadioGroup>
                        </div>
                      </>
                    )}
                  </Box>
                </>
              );
            })}
          </Box>

          <Box display={'flex'} justifyContent={'flex-end'}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitSelectedNode}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default memo(InputNodeDisplay);
